import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi, buildQueryString } from '@/lib/api/client';
import { feedbackKeys } from '@/hooks/api/query-keys';
import { usePermissions } from '@/hooks/use-permissions';
import type {
  FeedbackTicket,
  FeedbackStats,
  FeedbackFilter,
  FeedbackThreadMessage,
  FeedbackStatus,
  FeedbackPriority,
  FeedbackCategory,
} from '@/lib/types/feedback';

// ============ Query Hooks ============

/**
 * May the signed-in person read the feedback desk? GET /feedback and
 * /feedback/stats check read:Feedback, which only the company tier holds.
 *
 * TRUE ONLY ON A KNOWN YES — unknown (grants loading, or the fetch failed)
 * counts as no. Unlike the role gates on controls, which fail open, this
 * guards background polls: failing open would only mean a 403 every 30 s for
 * every workspace-tier role (role review, 2026-10-05). Filing feedback
 * (create:Feedback) is every role's and is not affected.
 */
export function useCanReadFeedback(): boolean {
  const { can } = usePermissions();
  return can('read', 'Feedback') === true;
}

export function useFeedbackTickets(filter?: FeedbackFilter) {
  const canRead = useCanReadFeedback();
  return useQuery({
    queryKey: feedbackKeys.list(filter as Record<string, unknown>),
    queryFn: async () => {
      const qs = filter ? buildQueryString(filter as Record<string, unknown>) : '';
      const result = await fetchApi<
        { data: FeedbackTicket[]; total: number; page: number; limit: number } | FeedbackTicket[]
      >(`/feedback${qs}`);
      // Normalize: handle both array and { data: [...] } shapes
      if (Array.isArray(result)) {
        return { data: result, total: result.length, page: 1, limit: result.length };
      }
      return result;
    },
    select: (result) => ({
      ...result,
      data: result.data.map((t) => ({
        ...t,
        acceptanceCriteria: t.acceptanceCriteria ?? [],
        externalLinks: t.externalLinks ?? [],
      })),
    }),
    enabled: canRead,
    refetchInterval: 30_000, // Poll every 30s for new tickets / status changes
  });
}

// Infinite-scroll variant for the chat-style /feedback sidebar — accumulates
// pages instead of capping at a single fetch. Fixes the "only 50 tickets ever
// visible" bug reported 2026-05-08.
export function useInfiniteFeedbackTickets(
  filter?: Omit<FeedbackFilter, 'page' | 'limit'>,
  pageSize: number = 50,
) {
  const canRead = useCanReadFeedback();
  return useInfiniteQuery({
    queryKey: feedbackKeys.list({ ...(filter ?? {}), infinite: true, pageSize } as Record<string, unknown>),
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const params: Record<string, unknown> = {
        ...(filter as Record<string, unknown> | undefined),
        page: pageParam,
        limit: pageSize,
      };
      const qs = buildQueryString(params);
      const result = await fetchApi<
        { data: FeedbackTicket[]; total: number; page: number; limit: number } | FeedbackTicket[]
      >(`/feedback${qs}`);
      if (Array.isArray(result)) {
        return { data: result, total: result.length, page: pageParam, limit: result.length };
      }
      return result;
    },
    getNextPageParam: (lastPage) => {
      const loaded = lastPage.page * lastPage.limit;
      return loaded < lastPage.total ? lastPage.page + 1 : undefined;
    },
    enabled: canRead,
    refetchInterval: 30_000,
  });
}

export function useFeedbackTicket(id: string | undefined) {
  return useQuery({
    queryKey: feedbackKeys.detail(id!),
    queryFn: () => fetchApi<FeedbackTicket>(`/feedback/${id}`),
    enabled: !!id,
    select: (t) => ({
      ...t,
      acceptanceCriteria: t.acceptanceCriteria ?? [],
      externalLinks: t.externalLinks ?? [],
    }),
  });
}

export function useFeedbackStats() {
  const canRead = useCanReadFeedback();
  return useQuery({
    queryKey: feedbackKeys.stats(),
    queryFn: () => fetchApi<FeedbackStats>('/feedback/stats'),
    enabled: canRead,
    refetchInterval: 30_000, // Poll every 30s for updated counts
  });
}

export function useFeedbackThread(ticketId: string | undefined) {
  return useQuery({
    queryKey: feedbackKeys.thread(ticketId!),
    queryFn: () => fetchApi<FeedbackThreadMessage[]>(`/feedback/${ticketId}/thread`),
    enabled: !!ticketId,
    select: (messages) => messages.map((m) => ({ ...m })),
  });
}

// ============ Mutation Hooks ============

export function useCreateFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      feedback: string;
      category?: FeedbackCategory;
      priority?: FeedbackPriority;
      url?: string;
      pathname?: string;
    }) =>
      fetchApi<FeedbackTicket>('/feedback', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feedbackKeys.all });
    },
  });
}

export function useUpdateFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<{
        status: FeedbackStatus;
        priority: FeedbackPriority;
        category: FeedbackCategory;
        adminNotes: string;
        tldr: string;
        userStory: string;
        acceptanceCriteria: string[];
        affectedArea: string;
      }>;
    }) =>
      fetchApi<FeedbackTicket>(`/feedback/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    onSuccess: (updatedTicket, { id }) => {
      queryClient.setQueryData(feedbackKeys.detail(id), updatedTicket);
      queryClient.invalidateQueries({ queryKey: feedbackKeys.lists() });
      queryClient.invalidateQueries({ queryKey: feedbackKeys.stats() });
    },
  });
}

export function useDeleteFeedback() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<void>(`/feedback/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feedbackKeys.all });
    },
  });
}

export function useSendThreadMessage(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { message: string }) =>
      fetchApi<FeedbackThreadMessage>(`/feedback/${ticketId}/thread`, {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feedbackKeys.thread(ticketId) });
      queryClient.invalidateQueries({ queryKey: feedbackKeys.detail(ticketId) });
      queryClient.invalidateQueries({ queryKey: feedbackKeys.lists() });
    },
  });
}

export function useBulkFeedbackAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      ids: string[];
      action: 'update_status' | 'delete';
      status?: FeedbackStatus;
    }) =>
      fetchApi<{ affected: number }>('/feedback/bulk-action', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: feedbackKeys.all });
    },
  });
}

export function useGenerateDevSpec() {
  return useMutation({
    mutationFn: (id: string) =>
      fetchApi<{ content: string; ticketId: string }>(`/feedback/${id}/dev-spec`, {
        method: 'POST',
      }),
  });
}
